import { memo } from "react";
import { useWatch, type UseFormReturn } from "react-hook-form";
import {
  Shirt,
  GraduationCap,
  User,
  FileText,
  CalendarClock,
  Package,
  Wallet,
} from "lucide-react";
import { formatCurrency } from "@/shared/lib/formatters";
import { cn } from "@/shared/lib/utils";
import { RentalType } from "@/shared/types/enums";
import { formatDatetimeLocalLabel } from "@/shared/lib/date-format";
import type { RentalCreateFormSchema } from "../rental-create-schema";

interface RentalSummaryPanelProps {
  form: UseFormReturn<RentalCreateFormSchema>;
  sellerLabel: string | null;
  clientLabel: string | null;
  contractLabel: string | null;
  /**
   * Formata as datas do período. Default: `datetime-local` (fluxo USER).
   * O fluxo ADMIN usa datas sem hora e passa o formatador correspondente.
   */
  formatPeriodLabel?: (value: string) => string | null;
}

export const RentalSummaryPanel = memo(function RentalSummaryPanel({
  form,
  sellerLabel,
  clientLabel,
  contractLabel,
  formatPeriodLabel = formatDatetimeLocalLabel,
}: RentalSummaryPanelProps) {
  const rentalType = useWatch({ control: form.control, name: "rentalType" });
  const newClient = useWatch({ control: form.control, name: "newClient" });
  const newContract = useWatch({ control: form.control, name: "newContract" });
  const expectedOutAt = useWatch({
    control: form.control,
    name: "expectedOutAt",
  });
  const expectedInAt = useWatch({
    control: form.control,
    name: "expectedInAt",
  });
  const stockIds = useWatch({ control: form.control, name: "stockIds" });
  const gownItems = useWatch({ control: form.control, name: "gownItems" });
  const totalValue = useWatch({ control: form.control, name: "totalValue" });

  const isSuit = rentalType === RentalType.SUIT;
  const TypeIcon = isSuit ? Shirt : GraduationCap;

  const clientValue = clientLabel ?? newClient?.name ?? null;
  const contractValue =
    contractLabel ?? (newContract ? `Nº ${newContract.contractNumber}` : null);

  const out = formatPeriodLabel(expectedOutAt);
  const back = formatPeriodLabel(expectedInAt);
  const periodValue = out && back ? `${out} → ${back}` : null;

  const itemCount = isSuit ? stockIds.length : gownItems.length;
  const itemValue =
    itemCount > 0
      ? isSuit
        ? `${itemCount} peça${itemCount > 1 ? "s" : ""}`
        : `${itemCount} ${itemCount > 1 ? "itens" : "item"}`
      : null;

  const totalValueLabel = totalValue > 0 ? formatCurrency(totalValue) : null;

  return (
    <div className="rounded-lg border bg-card lg:sticky lg:top-6 h-fit">
      <div className="px-4 py-3 border-b">
        <h3 className="text-sm font-semibold">Resumo</h3>
      </div>
      <dl className="divide-y">
        <SummaryRow
          icon={<TypeIcon className="w-4 h-4" />}
          label="Tipo"
          value={isSuit ? "Aluguel de Terno" : "Aluguel de Beca"}
        />
        <SummaryRow
          icon={<User className="w-4 h-4" />}
          label="Vendedor"
          value={sellerLabel}
        />
        <SummaryRow
          icon={<User className="w-4 h-4" />}
          label="Cliente"
          value={clientValue}
        />
        <SummaryRow
          icon={<FileText className="w-4 h-4" />}
          label="Contrato"
          value={contractValue}
        />
        <SummaryRow
          icon={<CalendarClock className="w-4 h-4" />}
          label="Período"
          value={periodValue}
        />
        <SummaryRow
          icon={<Package className="w-4 h-4" />}
          label={isSuit ? "Peças" : "Itens"}
          value={itemValue}
          placeholder="Opcional"
        />
        <SummaryRow
          icon={<Wallet className="w-4 h-4" />}
          label="Valor total"
          value={totalValueLabel}
        />
      </dl>
    </div>
  );
});

interface SummaryRowProps {
  icon: React.ReactNode;
  label: string;
  value: string | null;
  placeholder?: string;
}

function SummaryRow({
  icon,
  label,
  value,
  placeholder = "Pendente",
}: SummaryRowProps) {
  const filled = Boolean(value);
  return (
    <div className="flex items-start gap-3 px-4 py-2.5">
      <span
        className={cn(
          "mt-0.5 shrink-0",
          filled ? "text-primary" : "text-muted-foreground/50",
        )}
      >
        {icon}
      </span>
      <div className="min-w-0 flex-1">
        <dt className="text-xs text-muted-foreground">{label}</dt>
        <dd
          className={cn(
            "text-sm truncate",
            filled ? "font-medium text-foreground" : "text-muted-foreground/60",
          )}
        >
          {value ?? placeholder}
        </dd>
      </div>
    </div>
  );
}
